import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardNarrativePanel } from "src/components/common";
import { useMockAiSummaryUsage } from "src/hooks/useMockAiSummaryUsage";
import { isArabicLanguage } from "src/utils/numberFormat";
import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";
import type { PlatformReportBundle } from "../../data/paidMediaPlatformReportMockData";
import {
  formatOverviewAmount,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewPct,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import { generatePlatformReportExecutiveSummary } from "./platformReportAiSummary.data";
import { PLATFORM_REPORT_CURRENCY } from "./platformReportFormat";

interface PlatformReportAiSummaryProps {
  platform: PaidMediaOverviewPlatform;
  platformLabel: string;
  clickLabel: string;
  purchasesLabel: string;
  report: PlatformReportBundle;
  startDate: string;
  endDate: string;
}

const AI_SUMMARY_STORAGE_KEY_PREFIX = "paid_media_platform_ai_summary_enabled_";

const readStoredSummaryEnabled = (platform: PaidMediaOverviewPlatform): boolean => {
  try {
    return localStorage.getItem(`${AI_SUMMARY_STORAGE_KEY_PREFIX}${platform}`) !== "false";
  } catch {
    return true;
  }
};

const writeStoredSummaryEnabled = (platform: PaidMediaOverviewPlatform, enabled: boolean) => {
  try {
    localStorage.setItem(`${AI_SUMMARY_STORAGE_KEY_PREFIX}${platform}`, String(enabled));
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — the toggle
    // still works for the session, it just won't persist across reloads.
  }
};

const PlatformReportAiSummary: FC<PlatformReportAiSummaryProps> = ({
  platform,
  platformLabel,
  clickLabel,
  purchasesLabel,
  report,
  startDate,
  endDate,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = isArabicLanguage(i18n.language);
  const [analyzing, setAnalyzing] = useState(false);
  const [summaryEnabled, setSummaryEnabled] = useState(() => readStoredSummaryEnabled(platform));

  const handleSummaryEnabledChange = useCallback(
    (enabled: boolean) => {
      setSummaryEnabled(enabled);
      writeStoredSummaryEnabled(platform, enabled);
    },
    [platform]
  );

  const filterKey = `${platform}|${startDate}|${endDate}`;

  useEffect(() => {
    if (!summaryEnabled) {
      setAnalyzing(false);
      return;
    }
    setAnalyzing(true);
    const id = window.setTimeout(() => setAnalyzing(false), 420);
    return () => window.clearTimeout(id);
  }, [filterKey, summaryEnabled]);

  const summaryUsage = useMockAiSummaryUsage(`paid_media_${platform}`, filterKey, summaryEnabled);

  const fmt = useMemo(
    () => ({
      formatMoney: (value: number | null) => formatOverviewAmount(value, PLATFORM_REPORT_CURRENCY),
      formatUnitCost: (value: number | null) =>
        formatOverviewUnitCost(value, PLATFORM_REPORT_CURRENCY),
      formatCount: (value: number | null) => formatOverviewNumber(value),
      formatPercent: (value: number | null) => formatOverviewPct(value, 2),
      formatRoas: (value: number | null) => formatOverviewMultiplier(value),
      isRTL,
    }),
    [isRTL]
  );

  const summary = generatePlatformReportExecutiveSummary({
    platformLabel,
    clickLabel,
    purchasesLabel,
    report,
    startDate,
    endDate,
    fmt,
  });

  return (
    <DashboardNarrativePanel
      narrative={summary.hasData ? summary : null}
      loading={analyzing}
      defaultCollapsed
      title={t("platformAdsReport.aiSummary.title")}
      subtitle={
        summary.periodLabel
          ? t("platformAdsReport.aiSummary.subtitle", { period: summary.periodLabel })
          : undefined
      }
      aiSummary={{
        enabled: summaryEnabled,
        featureAvailable: true,
        setEnabled: handleSummaryEnabledChange,
        usage: summaryUsage,
      }}
    />
  );
};

export default PlatformReportAiSummary;
